import {
  Column,
  Text,
  Box,
  FileIcon,
  Heading,
  ImageIcon,
  Row,
  TableIcon,
} from "@hightouch/htui";

const FILE_KINDS = [
  { icon: FileIcon, color: "blue.500" },
  { icon: TableIcon, color: "green.500" },
  { icon: ImageIcon, color: "purple.500" },
];

export const FileDragOverlay = ({
  isVisible,
  heading = "Add files to chat",
  description = "Drop files here to include them in the conversation",
  compactDescription = description,
  treatment = "default",
}: {
  isVisible: boolean;
  heading?: string;
  description?: string;
  compactDescription?: string;
  /** Project treatment is opt-in; existing Agents consumers retain their card. */
  treatment?: "default" | "project";
}) => {
  if (treatment === "project")
    return (
      <Column
        position="absolute"
        inset={0}
        zIndex="docked"
        role="status"
        aria-hidden={!isVisible}
        pointerEvents="none"
        isolation="isolate"
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          bg: "base.lightBackground",
          opacity: 0.94,
          zIndex: -1,
        }}
        _after={{
          content: '""',
          position: "absolute",
          inset: 2,
          borderWidth: "1.5px",
          borderStyle: "dashed",
          borderColor: "primary.base",
          borderRadius: "5xl",
          opacity: 0.6,
          zIndex: -1,
        }}
        p={0}
        gap={2}
        alignItems="center"
        justifyContent="center"
        opacity={isVisible ? 1 : 0}
        visibility={isVisible ? "visible" : "hidden"}
        transitionProperty="opacity, visibility"
        transitionDuration="fast"
        transitionTimingFunction="easeOutQuad"
        _mediaReduceMotion={{ transitionProperty: "none" }}
        // Container queries preserve a bounded compact hint when the chat dock leaves little canvas space.
        sx={{
          containerType: "inline-size",
          containerName: "project-file-drop",
        }}
      >
        <Box
          position="absolute"
          inset={2}
          zIndex={-1}
          borderRadius="5xl"
          bg="primary.base"
          opacity={0.04}
        />
        <ProjectFileUploadView
          heading={heading}
          description={description}
          compactDescription={compactDescription}
        />
      </Column>
    );
  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={9999}
      aria-hidden={!isVisible}
      bg="blackAlpha.40"
      display="flex"
      alignItems="center"
      justifyContent="center"
      pointerEvents="none"
      opacity={isVisible ? 1 : 0}
      visibility={isVisible ? "visible" : "hidden"}
      transitionDuration="normal"
      transitionTimingFunction="easeOutQuart"
      transitionProperty="opacity, visibility"
      backdropFilter="blur(4px)"
    >
      <Column
        bg="white"
        borderRadius="2xl"
        p={6}
        gap={2}
        alignItems="center"
        boxShadow="lg"
        maxWidth="sm"
        pointerEvents="none"
        transform={isVisible ? "scale(1)" : "scale(0.95)"}
        transitionDuration="slow"
        transitionTimingFunction="easeOutQuad"
        transitionProperty="transform"
        _mediaReduceMotion={{ transform: "none", transitionProperty: "none" }}
      >
        <Box as={FileIcon} boxSize="32px" color="teal.500" />
        <Column gap={1} alignItems="center" textAlign="center">
          <Heading size="md" color="text.primary">
            {heading}
          </Heading>
          <Text color="text.secondary">{description}</Text>
        </Column>
      </Column>
    </Box>
  );
};

/** The project upload artwork and copy shared by physical drops and explicit upload. */
export function ProjectFileUploadView({
  heading,
  description,
  compactDescription = description,
}: {
  heading: string;
  description: string;
  compactDescription?: string;
}) {
  return (
    <Column
      alignItems="center"
      gap={2}
      minWidth={0}
      width="100%"
      p={8}
      textAlign="center"
      sx={{
        "@container project-file-drop (max-width: 159px)": {
          padding: 1,
          ".file-drop-details": { display: "none" },
          ".file-drop-compact": { display: "block" },
        },
      }}
    >
      <Row className="file-drop-details" gap={1} aria-hidden>
        {FILE_KINDS.map(({ icon, color }) => (
          <Row
            key={color}
            boxSize={7}
            justifyContent="center"
            alignItems="center"
            bg="white"
            borderRadius="full"
            boxShadow="xs"
          >
            <Box as={icon} boxSize={4} color={color} />
          </Row>
        ))}
      </Row>
      <Text
        className="file-drop-details"
        fontWeight="medium"
        color="text.primary"
      >
        {heading}
      </Text>
      <Text className="file-drop-details" size="sm" color="text.secondary">
        {description}
      </Text>
      <Box
        className="file-drop-compact"
        display="none"
        sx={{ writingMode: "vertical-rl", whiteSpace: "nowrap" }}
      >
        <Text size="sm">{compactDescription}</Text>
      </Box>
    </Column>
  );
}
